import React, { useState, useEffect } from 'react';
import { Calendar, CalendarDays, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { ReservationService } from '../services/ReservationService';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

interface CalendarViewProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  disabledDates?: number[];
  onRefreshCalendar?: () => Promise<void>;
}

// Função para forçar atualização dos dias indisponíveis
export const refreshCalendarDays = async (currentMonth: Date): Promise<number[]> => {
  try {
    const diasDesabilitados = await ReservationService.obterDiasSemAgenda(currentMonth);
    return diasDesabilitados;
  } catch (error) {
    console.error("Erro ao atualizar dias do calendário:", error);
    return [];
  }
};

export const CalendarView: React.FC<CalendarViewProps> = ({
  selectedDate,
  onDateChange,
  disabledDates = [],
  onRefreshCalendar
}) => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));
  const [disabledDays, setDisabledDays] = useState<number[]>(disabledDates);
// Removed unused state variable 'anoExercicio'
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [maxDate, setMaxDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // {|INICIO| 12/06/2026 às 01:10 - CALENDARIO: CONTROLAR ESTADO DO BOTAO DE ATUALIZAR MANUALMENTE}
  const [estaAtualizandoCalendario, setEstaAtualizandoCalendario] = useState(false);
  // {|FIM| 12/06/2026 às 01:10 - CALENDARIO: CONTROLAR ESTADO DO BOTAO DE ATUALIZAR MANUALMENTE}

  // Função para atualizar dias desabilitados externamente
  const refreshDisabledDays = async () => {
    try {
      const diasDesabilitados = await ReservationService.obterDiasSemAgenda(currentMonth);
      setDisabledDays(diasDesabilitados);
      
      // Chama callback se fornecido
      if (onRefreshCalendar) {
        await onRefreshCalendar();
      }
    } catch (error) {
      console.error("Erro ao atualizar dias desabilitados:", error);
    }
  };

  // Função interna para atualizar dias desabilitados quando necessário
  // A função refreshCalendarDays exportada é usada externamente

  const monthNames = [
    t('calendar.months.january'), t('calendar.months.february'), t('calendar.months.march'), 
    t('calendar.months.april'), t('calendar.months.may'), t('calendar.months.june'),
    t('calendar.months.july'), t('calendar.months.august'), t('calendar.months.september'), 
    t('calendar.months.october'), t('calendar.months.november'), t('calendar.months.december')
  ];

  const dayNames = [
    t('calendar.days.sunday'), t('calendar.days.monday'), t('calendar.days.tuesday'), 
    t('calendar.days.wednesday'), t('calendar.days.thursday'), t('calendar.days.friday'), 
    t('calendar.days.saturday')
  ];

  // Inicializar calendário com configurações do ano de exercício
  useEffect(() => {
    const inicializarCalendario = async () => {
      try {
        setIsLoading(true);

        // Obtém as configurações do sistema
        const anoExercicioObj = await ReservationService.getAnoExercicio();

        // Ajuste para compatibilidade com Safari e tolerância a nulos do legado
        const anoExercicio = Number(anoExercicioObj.ano_exercicio) || new Date().getFullYear();
        let data_inicio_agenda_online: Date;
        if (anoExercicioObj.data_inicio_agenda_online && typeof anoExercicioObj.data_inicio_agenda_online === 'string') {
          const dataStr = anoExercicioObj.data_inicio_agenda_online;
          const dataParts = dataStr.split('-');
          data_inicio_agenda_online = new Date(
            parseInt(dataParts[0], 10),
            parseInt(dataParts[1], 10) - 1,
            parseInt(dataParts[2], 10)
          );
        } else {
          // Fallback seguro: primeiro dia do mês atual
          const now = new Date();
          data_inicio_agenda_online = new Date(anoExercicio, now.getMonth(), 1);
        }

        // Definição do mês máximo: tolera nulo vindo do backend
        let mesMaximo: number;
        if (typeof anoExercicioObj.meses_lib_agenda_online === 'number' && !isNaN(anoExercicioObj.meses_lib_agenda_online)) {
          mesMaximo = Math.max(0, anoExercicioObj.meses_lib_agenda_online - 1);
        } else {
          // Fallback: permite navegação até ~6 meses à frente a partir do mês atual
          mesMaximo = Math.min(11, new Date().getMonth() + 5);
        }

        // Obtém o número do mês e ano atuais
        const hoje = new Date();
        const mesAtual = hoje.getMonth();
        const anoAtual = hoje.getFullYear();

        // Define o mês inicial baseado na comparação com a data atual (compatível com legado)
        let mesInicial = data_inicio_agenda_online.getMonth();
        if (anoExercicio === anoAtual && mesInicial < mesAtual) {
          mesInicial = mesAtual;
        }

        // Definir a data inicial
        let dataInicial = new Date(anoExercicio, mesInicial, 1);

        // Calcular o último dia do mês máximo (como na função JS original)
        const ultimoDiaMes = new Date(anoExercicio, mesMaximo + 1, 0);
        
        // Define as datas mínima e máxima
        setMinDate(dataInicial);
        setMaxDate(ultimoDiaMes);

        // {|INICIO| 11/06/2026 às 21:18 - COMPARTILHAMENTO: PRESERVAR MÊS DA DATA INFORMADA NA URL QUANDO VÁLIDA}
        const hojeSemHorario = new Date(hoje);
        hojeSemHorario.setHours(0, 0, 0, 0);
        const dataSelecionadaSemHorario = new Date(selectedDate);
        dataSelecionadaSemHorario.setHours(0, 0, 0, 0);
        const dataSelecionadaDentroDaJanela =
          dataSelecionadaSemHorario >= dataInicial &&
          dataSelecionadaSemHorario <= ultimoDiaMes &&
          dataSelecionadaSemHorario >= hojeSemHorario;
        const mesInicialCalendario = dataSelecionadaDentroDaJanela
          ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
          : new Date(dataInicial);

        // Atualiza o mês atual para o mês da data compartilhada quando ela estiver dentro da janela permitida
        setCurrentMonth(mesInicialCalendario);

        // Obtém os dias desabilitados para o mês efetivamente exibido
        const diasDesabilitados = await ReservationService.obterDiasSemAgenda(mesInicialCalendario);
        setDisabledDays(diasDesabilitados);
        // {|FIM| 11/06/2026 às 21:18 - COMPARTILHAMENTO: PRESERVAR MÊS DA DATA INFORMADA NA URL QUANDO VÁLIDA}

        // Encontra o primeiro dia válido para seleção
        while (diasDesabilitados.indexOf(dataInicial.getDate()) !== -1) {
          dataInicial.setDate(dataInicial.getDate() + 1);
          // Se passou para o próximo mês, para de procurar
          if (dataInicial.getMonth() !== mesInicial) {
            dataInicial = new Date(anoExercicio, mesInicial, 1);
            break;
          }
        }

        // Se a data selecionada não for válida, seleciona a data inicial
        if (selectedDate < dataInicial || selectedDate > ultimoDiaMes) {
          onDateChange(dataInicial);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Erro ao inicializar o calendário:", error);
        setIsLoading(false);
      }
    };

    inicializarCalendario();
  }, []);

  // Reagir a eventos globais de login/logoff para reinicializar calendário
  useEffect(() => {
    const handleLogin = async () => {
      try {
        await refreshDisabledDays();
        // Recalcula limites com base no novo contexto (idcliente)
        const cfg = await ReservationService.getAnoExercicio();
        const anoExercicio = Number(cfg.ano_exercicio) || new Date().getFullYear();
        const mesesLib = (typeof cfg.meses_lib_agenda_online === 'number' && !isNaN(cfg.meses_lib_agenda_online))
          ? cfg.meses_lib_agenda_online
          : Math.min(11, new Date().getMonth() + 5);
        const mesMaximo = Math.max(0, mesesLib - 1);
        const hoje = new Date();
        const mesAtual = hoje.getMonth();
        const anoAtual = hoje.getFullYear();
        let dataInicio: Date;
        if (cfg.data_inicio_agenda_online && typeof cfg.data_inicio_agenda_online === 'string') {
          const p = cfg.data_inicio_agenda_online.split('-');
          dataInicio = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
        } else {
          dataInicio = new Date(anoExercicio, mesAtual, 1);
        }
        let mesInicial = dataInicio.getMonth();
        if (anoExercicio === anoAtual && mesInicial < mesAtual) mesInicial = mesAtual;
        const min = new Date(anoExercicio, mesInicial, 1);
        const max = new Date(anoExercicio, mesMaximo + 1, 0);
        setMinDate(min);
        setMaxDate(max);
        setCurrentMonth(new Date(min));
        // Seleciona próximo dia disponível do mês corrente
        const dias = await ReservationService.obterDiasSemAgenda(min);
        setDisabledDays(dias);
        const next = findNextAvailableDate(min, mesAtual === min.getMonth() ? hoje.getDate() : 1, dias);
        if (next) onDateChange(next);
      } catch (e) {
        console.error('Erro ao reagir a userLogin:', e);
      }
    };
    const handleLogout = async () => {
      try {
        await refreshDisabledDays();
        // Após logoff, mantém as mesmas regras de janela mas ignora idcliente
        const cfg = await ReservationService.getAnoExercicio();
        const anoExercicio = Number(cfg.ano_exercicio) || new Date().getFullYear();
        const mesesLib = (typeof cfg.meses_lib_agenda_online === 'number' && !isNaN(cfg.meses_lib_agenda_online))
          ? cfg.meses_lib_agenda_online
          : Math.min(11, new Date().getMonth() + 5);
        const mesMaximo = Math.max(0, mesesLib - 1);
        const hoje = new Date();
        const mesAtual = hoje.getMonth();
        const anoAtual = hoje.getFullYear();
        let dataInicio: Date;
        if (cfg.data_inicio_agenda_online && typeof cfg.data_inicio_agenda_online === 'string') {
          const p = cfg.data_inicio_agenda_online.split('-');
          dataInicio = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
        } else {
          dataInicio = new Date(anoExercicio, mesAtual, 1);
        }
        let mesInicial = dataInicio.getMonth();
        if (anoExercicio === anoAtual && mesInicial < mesAtual) mesInicial = mesAtual;
        const min = new Date(anoExercicio, mesInicial, 1);
        const max = new Date(anoExercicio, mesMaximo + 1, 0);
        setMinDate(min);
        setMaxDate(max);
        setCurrentMonth(new Date(min));
        const dias = await ReservationService.obterDiasSemAgenda(min);
        setDisabledDays(dias);
        const next = findNextAvailableDate(min, 1, dias);
        if (next) onDateChange(next);
      } catch (e) {
        console.error('Erro ao reagir a userLogout:', e);
      }
    };
    window.addEventListener('userLogin', handleLogin as EventListener);
    window.addEventListener('userLogout', handleLogout as EventListener);
    return () => {
      window.removeEventListener('userLogin', handleLogin as EventListener);
      window.removeEventListener('userLogout', handleLogout as EventListener);
    };
  }, []);

  // Atualiza os dias desabilitados sempre que o mês atual mudar
  useEffect(() => {
    const atualizarDiasDesabilitados = async () => {
      try {
        const diasDesabilitados = await ReservationService.obterDiasSemAgenda(currentMonth);
        setDisabledDays(diasDesabilitados);
        
        // Após atualizar os dias desabilitados, aplica a lógica de seleção inteligente
        
        const today = new Date();
        const isCurrentMonth = currentMonth.getFullYear() === today.getFullYear() && 
                              currentMonth.getMonth() === today.getMonth();
        
        let targetDate: Date | null = null;
        // {|INICIO| 11/06/2026 às 21:18 - COMPARTILHAMENTO: EVITAR SOBRESCREVER DATA DA URL QUANDO O DIA JÁ É VÁLIDO}
        const dataSelecionadaNoMesAtual =
          selectedDate.getFullYear() === currentMonth.getFullYear() &&
          selectedDate.getMonth() === currentMonth.getMonth();
        const hojeSemHorario = new Date();
        hojeSemHorario.setHours(0, 0, 0, 0);
        const dataSelecionadaSemHorario = new Date(selectedDate);
        dataSelecionadaSemHorario.setHours(0, 0, 0, 0);
        const dataSelecionadaDentroDosLimites =
          (!minDate || selectedDate >= minDate) &&
          (!maxDate || selectedDate <= maxDate) &&
          dataSelecionadaSemHorario >= hojeSemHorario;
        const dataSelecionadaDisponivel = !diasDesabilitados.includes(selectedDate.getDate());

        if (dataSelecionadaNoMesAtual && dataSelecionadaDentroDosLimites && dataSelecionadaDisponivel) {
          return;
        }
        // {|FIM| 11/06/2026 às 21:18 - COMPARTILHAMENTO: EVITAR SOBRESCREVER DATA DA URL QUANDO O DIA JÁ É VÁLIDO}
        
        if (isCurrentMonth) {
          // Para o mês corrente: tenta o dia atual, senão o próximo disponível
          targetDate = findNextAvailableDate(currentMonth, today.getDate(), diasDesabilitados);
        } else {
          // Para outros meses: começa do dia 1 ou próximo disponível
          targetDate = findNextAvailableDate(currentMonth, 1, diasDesabilitados);
        }
        
        if (targetDate) {
          onDateChange(targetDate);
        }
        
      } catch (error) {
        console.error("Erro ao obter dias desabilitados:", error);
      }
    };

    if (currentMonth) {
      atualizarDiasDesabilitados();
    }
  }, [currentMonth]);

  // {|INICIO| 11/06/2026 às 20:47 - COMPARTILHAMENTO: SINCRONIZAR MÊS EXIBIDO COM A DATA RECEBIDA PELA URL}
  useEffect(() => {
    const primeiroDiaMesSelecionado = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const primeiroDiaMinimo = minDate ? new Date(minDate.getFullYear(), minDate.getMonth(), 1) : null;
    const primeiroDiaMaximo = maxDate ? new Date(maxDate.getFullYear(), maxDate.getMonth(), 1) : null;

    if (primeiroDiaMinimo && primeiroDiaMesSelecionado < primeiroDiaMinimo) return;
    if (primeiroDiaMaximo && primeiroDiaMesSelecionado > primeiroDiaMaximo) return;

    const mesJaSelecionado =
      currentMonth.getFullYear() === primeiroDiaMesSelecionado.getFullYear() &&
      currentMonth.getMonth() === primeiroDiaMesSelecionado.getMonth();

    if (!mesJaSelecionado) {
      setCurrentMonth(primeiroDiaMesSelecionado);
    }
  }, [maxDate, minDate, selectedDate]);
  // {|FIM| 11/06/2026 às 20:47 - COMPARTILHAMENTO: SINCRONIZAR MÊS EXIBIDO COM A DATA RECEBIDA PELA URL}

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);

    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    
    // Não permitir datas retroativas (dias anteriores ao dia atual)
    const isBeforeToday = dateToCheck < today;
    
    // Verificar se o dia está fora do mês atual sendo visualizado
    const isOutsideCurrentMonth = dateToCheck.getMonth() !== currentMonth.getMonth() || 
                                  dateToCheck.getFullYear() !== currentMonth.getFullYear();
    
    // Verificar limites de data mínima e máxima
    const isBeforeMinDate = minDate && dateToCheck < minDate;
    const isAfterMaxDate = maxDate && dateToCheck > maxDate;
    
    // Verificar se o dia está na lista de dias sem agenda
    const isDayWithoutAgenda = disabledDays.includes(dateToCheck.getDate());
    
    return (
      isBeforeToday ||
      isOutsideCurrentMonth ||
      isBeforeMinDate ||
      isAfterMaxDate ||
      isDayWithoutAgenda
    );
  };

  const isDateSelected = (date: Date) => {
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const handleDateClick = (date: Date) => {
    if (!isDateDisabled(date)) {
      onDateChange(date);
    }
  };

  // Função para encontrar o próximo dia disponível em um mês
  const findNextAvailableDate = (targetMonth: Date, startDay?: number, diasDesabilitadosParam?: number[]): Date | null => {
    const year = targetMonth.getFullYear();
    const month = targetMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Se startDay não for fornecido, usa 1 (primeiro dia do mês)
    const startFrom = startDay || 1;
    
    // Usa os dias desabilitados passados como parâmetro ou os do estado
    const diasDesabilitadosToUse = diasDesabilitadosParam || disabledDays;
    
    for (let day = startFrom; day <= daysInMonth; day++) {
      const testDate = new Date(year, month, day);
      
      // Verifica se a data está dentro dos limites permitidos
      if (minDate && testDate < minDate) continue;
      if (maxDate && testDate > maxDate) continue;
      
      // Verifica se o dia não está na lista de dias desabilitados
      if (!diasDesabilitadosToUse.includes(day)) {
        return testDate;
      }
    }
    
    return null; // Nenhum dia disponível encontrado
  };

  const handlePreviousMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    
    // Verifica se o novo mês está dentro dos limites permitidos
    if (minDate && (newMonth.getFullYear() < minDate.getFullYear() || 
        (newMonth.getFullYear() === minDate.getFullYear() && newMonth.getMonth() < minDate.getMonth()))) {
      return;
    }
    
    // Apenas muda o mês - a lógica de seleção será executada no useEffect
    setCurrentMonth(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    
    // Verifica se o novo mês está dentro dos limites permitidos
    if (maxDate && (newMonth.getFullYear() > maxDate.getFullYear() || 
        (newMonth.getFullYear() === maxDate.getFullYear() && newMonth.getMonth() > maxDate.getMonth()))) {
      return;
    }
    
    // Apenas muda o mês - a lógica de seleção será executada no useEffect
    setCurrentMonth(newMonth);
  };

  // {|INICIO| 11/06/2026 às 21:23 - CALENDARIO: ADICIONAR BOTAO HOJE PARA RETORNAR AO DIA ATUAL}
  // Função para comparar duas datas considerando apenas dia, mês e ano.
  const isMesmoDia = (primeiraData: Date, segundaData: Date) => {
    return (
      primeiraData.getDate() === segundaData.getDate() &&
      primeiraData.getMonth() === segundaData.getMonth() &&
      primeiraData.getFullYear() === segundaData.getFullYear()
    );
  };

  const dataHoje = new Date();
  const dataHojeSemHorario = new Date(dataHoje.getFullYear(), dataHoje.getMonth(), dataHoje.getDate());
  const dataMinimaSemHorario = minDate
    ? new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
    : null;
  const dataMaximaSemHorario = maxDate
    ? new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())
    : null;
  const hojeEstaDentroDaJanelaPermitida =
    (!dataMinimaSemHorario || dataHojeSemHorario >= dataMinimaSemHorario) &&
    (!dataMaximaSemHorario || dataHojeSemHorario <= dataMaximaSemHorario);
  const mesExibidoEhMesAtual =
    currentMonth.getMonth() === dataHoje.getMonth() &&
    currentMonth.getFullYear() === dataHoje.getFullYear();
  const diaSelecionadoEhHoje = isMesmoDia(selectedDate, dataHojeSemHorario);
  const exibirBotaoHoje = hojeEstaDentroDaJanelaPermitida && (!mesExibidoEhMesAtual || !diaSelecionadoEhHoje);

  // Função para reposicionar o calendário no mês atual e selecionar o dia de hoje.
  const handleIrParaHoje = () => {
    if (!hojeEstaDentroDaJanelaPermitida) {
      return;
    }

    const inicioMesAtual = new Date(dataHoje.getFullYear(), dataHoje.getMonth(), 1);
    setCurrentMonth(inicioMesAtual);
    onDateChange(dataHojeSemHorario);
  };
  // {|FIM| 11/06/2026 às 21:23 - CALENDARIO: ADICIONAR BOTAO HOJE PARA RETORNAR AO DIA ATUAL}

  // {|INICIO| 12/06/2026 às 01:10 - CALENDARIO: ADICIONAR ATUALIZACAO MANUAL DOS DIAS E HORARIOS}
  // Função para recarregar manualmente os dias indisponíveis e os horários da data selecionada.
  const handleAtualizarCalendario = async () => {
    if (estaAtualizandoCalendario || isLoading) {
      return;
    }

    try {
      setEstaAtualizandoCalendario(true);
      await refreshDisabledDays();
    } catch (error) {
      console.error('Erro ao atualizar manualmente o calendário:', error);
    } finally {
      setEstaAtualizandoCalendario(false);
    }
  };
  // {|FIM| 12/06/2026 às 01:10 - CALENDARIO: ADICIONAR ATUALIZACAO MANUAL DOS DIAS E HORARIOS}

  const days = getDaysInMonth(currentMonth);

  // Verifica se os botões de navegação devem estar desabilitados
  const isPreviousDisabled = minDate && (
    currentMonth.getFullYear() < minDate.getFullYear() || 
    (currentMonth.getFullYear() === minDate.getFullYear() && currentMonth.getMonth() < minDate.getMonth())
  );
  const isNextDisabled = maxDate && (
    currentMonth.getFullYear() > maxDate.getFullYear() || 
    (currentMonth.getFullYear() === maxDate.getFullYear() && currentMonth.getMonth() > maxDate.getMonth())
  );

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center p-8">
        <div className={`${theme === 'dark' ? 'text-dark-secondary' : 'text-gray-500'}`}>
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* {|INICIO| 12/03/2026 às 20:16 - CALENDÁRIO: PERMITIR ESTICAR/OCUPAR ALTURA DISPONÍVEL NO DESKTOP} */}
      {/* {|INICIO| 12/03/2026 às 20:36 - CALENDÁRIO: USAR FLEX-1/MIN-H-0 PARA EVITAR ALTURA "AUTO" EM 100% ZOOM} */}
      {/* {|INICIO| 12/03/2026 às 20:38 - CALENDÁRIO: PADRONIZAR CARD E MOVER TÍTULO PARA DENTRO DO COMPONENTE} */}
      <div className={`rounded-lg shadow-lg p-3 sm:p-5 lg:p-4 border flex flex-col flex-1 min-h-0 ${
        theme === 'dark' 
          ? 'bg-dark-surface border-dark-border' 
          : 'bg-white border-gray-200'
      }`}>
        {/* {|INICIO| 11/06/2026 às 21:26 - CALENDARIO: ALINHAR BOTAO HOJE NA MESMA LINHA DO TITULO} */}
        <div className="mb-3 flex items-center justify-between gap-3 flex-shrink-0">
          <h2 className={`text-lg font-bold flex items-center space-x-2 ${
            theme === 'dark' ? 'text-dark-primary' : 'text-gray-900'
          }`}>
            <Calendar className="w-5 h-5 text-red-600" />
            <span>{t('calendar.selectDate')}</span>
          </h2>
          {/* {|INICIO| 12/06/2026 às 01:10 - CALENDARIO: INSERIR BOTAO DE ATUALIZAR AO LADO DO BOTAO HOJE} */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleAtualizarCalendario}
              type="button"
              disabled={estaAtualizandoCalendario || isLoading}
              title={t('calendar.refreshTooltip')}
              aria-label={t('calendar.refreshTooltip')}
              className={`p-1 sm:p-2 rounded-lg transition-colors ${
                estaAtualizandoCalendario || isLoading
                  ? 'opacity-50 cursor-not-allowed'
                  : theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-300'
                    : 'hover:bg-gray-100 text-gray-500'
              } flex-shrink-0`}
            >
              <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${estaAtualizandoCalendario ? 'animate-spin' : ''}`} />
            </button>
            {exibirBotaoHoje && (
              <button
                onClick={handleIrParaHoje}
                type="button"
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold transition-all duration-200 flex-shrink-0 ${
                  theme === 'dark'
                    ? 'border-red-700/60 bg-red-950/30 text-red-200 hover:bg-red-900/40 hover:border-red-600'
                    : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-300'
                }`}
              >
                <CalendarDays className="w-4 h-4" />
                <span>{t('calendar.today')}</span>
              </button>
            )}
          </div>
          {/* {|FIM| 12/06/2026 às 01:10 - CALENDARIO: INSERIR BOTAO DE ATUALIZAR AO LADO DO BOTAO HOJE} */}
        </div>
        {/* {|FIM| 11/06/2026 às 21:26 - CALENDARIO: ALINHAR BOTAO HOJE NA MESMA LINHA DO TITULO} */}
        {/* {|INICIO| 11/06/2026 às 21:23 - CALENDARIO: EXIBIR BOTAO HOJE NO CABECALHO DO CALENDARIO} */}
        <div className="mb-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <button
              onClick={handlePreviousMonth}
              disabled={isPreviousDisabled || false}
              className={`p-3 rounded-xl transition-all duration-200 ${
                isPreviousDisabled 
                  ? `${theme === 'dark' ? 'text-dark-muted cursor-not-allowed' : 'text-gray-300 cursor-not-allowed'}` 
                  : `${theme === 'dark' ? 'hover:bg-red-900/30 hover:text-red-300 hover:shadow-lg hover:scale-110' : 'hover:bg-red-50 hover:text-red-600 hover:shadow-md hover:scale-110'}`
              } group`}
            >
              <ChevronLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
            </button>
            
            <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-dark-primary' : 'text-gray-900'}`}>
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            
            <button
              onClick={handleNextMonth}
              disabled={isNextDisabled || false}
              className={`p-3 rounded-xl transition-all duration-200 ${
                isNextDisabled 
                  ? `${theme === 'dark' ? 'text-dark-muted cursor-not-allowed' : 'text-gray-300 cursor-not-allowed'}` 
                  : `${theme === 'dark' ? 'hover:bg-red-900/30 hover:text-red-300 hover:shadow-lg hover:scale-110' : 'hover:bg-red-50 hover:text-red-600 hover:shadow-md hover:scale-110'}`
              } group`}
            >
              <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
        {/* {|FIM| 11/06/2026 às 21:23 - CALENDARIO: EXIBIR BOTAO HOJE NO CABECALHO DO CALENDARIO} */}

        <div className="grid grid-cols-7 gap-2 flex-shrink-0">
          {dayNames.map((day) => (
            <div key={day} className={`p-2 text-center text-xs font-semibold uppercase tracking-wider ${
              theme === 'dark' ? 'text-dark-muted' : 'text-gray-500'
            }`}>
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2 mt-2 flex-1 min-h-0 auto-rows-fr">
          {days.map((day, index) => {
            const disabled = isDateDisabled(day);
            const selected = isDateSelected(day);
            const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

            return (
              <button
                key={index}
                onClick={() => handleDateClick(day)}
                disabled={disabled}
                className={`
                  w-full h-full p-2 text-sm font-medium transition-all duration-200 rounded-xl relative flex items-center justify-center overflow-hidden
                  ${disabled 
                    ? `${theme === 'dark' 
                        ? 'text-dark-muted cursor-not-allowed bg-dark-hover/30' 
                        : 'text-gray-300 cursor-not-allowed bg-gray-50'
                      }` 
                    : `${theme === 'dark'
                        ? 'text-dark-secondary hover:bg-red-900/30 hover:text-red-300 hover:shadow-lg hover:scale-105'
                        : 'text-gray-700 hover:bg-red-50 hover:text-red-600 hover:shadow-md hover:scale-105'
                      }`
                  }
                  ${selected 
                    ? `${theme === 'dark' 
                        ? 'bg-gradient-to-br from-red-700 to-red-800 text-white shadow-lg shadow-red-900/50 scale-105 ring-2 ring-red-600/50' 
                        : 'bg-gradient-to-br from-red-600 to-red-700 text-white shadow-lg shadow-red-600/30 scale-105 ring-2 ring-red-500/30'
                      }` 
                    : ''
                  }
                  ${!isCurrentMonth ? 'opacity-40' : ''}
                  ${isCurrentMonth && !disabled && !selected ? 
                    `${theme === 'dark' 
                      ? 'hover:ring-1 hover:ring-red-700/50' 
                      : 'hover:ring-1 hover:ring-red-500/30'
                    }` 
                    : ''
                  }
                `}
              >
                <span className="relative z-10">{day.getDate()}</span>
                {selected && (
                  <div className={`absolute inset-0 rounded-xl ${
                    theme === 'dark' 
                      ? 'bg-gradient-to-br from-red-600/20 to-red-800/20' 
                      : 'bg-gradient-to-br from-red-500/20 to-red-700/20'
                  } animate-pulse`}></div>
                )}
              </button>
            );
          })}
        </div>

        <div className={`mt-4 p-4 rounded-xl flex-shrink-0 ${
          theme === 'dark' 
            ? 'bg-dark-hover/30 border border-dark-border/50' 
            : 'bg-gray-50/50 border border-gray-200/50'
        }`}>
          <div className="flex items-center justify-center space-x-8">
            <div className="flex items-center space-x-2">
              <div className={`w-4 h-4 rounded-lg shadow-sm ${
                theme === 'dark' 
                  ? 'bg-gradient-to-br from-red-700 to-red-800 ring-1 ring-red-600/50' 
                  : 'bg-gradient-to-br from-red-600 to-red-700 ring-1 ring-red-500/30'
              }`}></div>
              <span className={`text-sm font-medium ${
                theme === 'dark' ? 'text-dark-secondary' : 'text-gray-700'
              }`}>
                {t('calendar.selected')}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-4 h-4 rounded-lg shadow-sm ${
                theme === 'dark' 
                  ? 'bg-dark-hover/50 border border-dark-border' 
                  : 'bg-gray-200 border border-gray-300'
              }`}></div>
              <span className={`text-sm font-medium ${
                theme === 'dark' ? 'text-dark-muted' : 'text-gray-500'
              }`}>
                {t('calendar.unavailable')}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* {|FIM| 12/03/2026 às 20:38 - CALENDÁRIO: PADRONIZAR CARD E MOVER TÍTULO PARA DENTRO DO COMPONENTE} */}
      {/* {|FIM| 12/03/2026 às 20:36 - CALENDÁRIO: USAR FLEX-1/MIN-H-0 PARA EVITAR ALTURA "AUTO" EM 100% ZOOM} */}
      {/* {|FIM| 12/03/2026 às 20:16 - CALENDÁRIO: PERMITIR ESTICAR/OCUPAR ALTURA DISPONÍVEL NO DESKTOP} */}
    </>
  );
};
